import { z } from 'zod';

import { apiClient } from './api-client';

export type RecurringInvoiceStatus = 'active' | 'paused' | 'cancelled';

const clientLiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  document: z.string().nullable(),
  email: z.string().nullable(),
});

const recurringInvoiceSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  amount: z.string(),
  serviceDescription: z.string(),
  billingDay: z.number().int(),
  nextEmissionDate: z.string(),
  status: z.enum(['active', 'paused', 'cancelled']),
  lastEmittedAt: z.string().nullable(),
  lastFiscalInvoiceId: z.string().uuid().nullable(),
  lastChargeId: z.string().uuid().nullable(),
  emissionCount: z.number().int(),
  lastError: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int(),
  client: clientLiteSchema.optional(),
});

const listResponseSchema = z.object({
  items: z.array(recurringInvoiceSchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.null(),
  }),
});

const singleResponseSchema = z.object({ data: recurringInvoiceSchema });

export type RecurringInvoice = z.infer<typeof recurringInvoiceSchema>;

export interface ListFilters {
  cursor?: string;
  limit?: number;
  status?: RecurringInvoiceStatus;
  client_id?: string;
}

export async function fetchRecurringInvoices(
  filters: ListFilters = {},
): Promise<{ items: RecurringInvoice[]; nextCursor: string | null }> {
  const { data } = await apiClient.get('/api/fiscal/recurring', { params: filters });
  const parsed = listResponseSchema.parse(data);
  return { items: parsed.items, nextCursor: parsed.meta.next_cursor };
}

export interface CreateRecurringInvoicePayload {
  client_id: string;
  amount: string;
  service_description: string;
  billing_day: number;
  emit_now: boolean;
  // Natureza fiscal própria (opcional) — trio all-or-nothing; ausente = software padrão.
  municipal_service_code?: string;
  municipal_service_name?: string;
  iss_rate?: number;
  /** Código NBS guardado no agendamento — vai em toda NF do mês. */
  nbs_code?: string;
}

export async function createRecurringInvoice(
  payload: CreateRecurringInvoicePayload,
): Promise<RecurringInvoice> {
  const { data } = await apiClient.post('/api/fiscal/recurring', payload);
  return singleResponseSchema.parse(data).data;
}

export interface UpdateRecurringInvoicePayload {
  version: number;
  amount?: string;
  service_description?: string;
  billing_day?: number;
}

export async function updateRecurringInvoice(
  id: string,
  payload: UpdateRecurringInvoicePayload,
): Promise<RecurringInvoice> {
  const { data } = await apiClient.patch(`/api/fiscal/recurring/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function pauseRecurringInvoice(
  id: string,
  version: number,
): Promise<RecurringInvoice> {
  const { data } = await apiClient.patch(`/api/fiscal/recurring/${id}/pause`, { version });
  return singleResponseSchema.parse(data).data;
}

export async function resumeRecurringInvoice(
  id: string,
  version: number,
): Promise<RecurringInvoice> {
  const { data } = await apiClient.patch(`/api/fiscal/recurring/${id}/resume`, { version });
  return singleResponseSchema.parse(data).data;
}

export async function cancelRecurringInvoice(
  id: string,
  version: number,
): Promise<RecurringInvoice> {
  const { data } = await apiClient.delete(`/api/fiscal/recurring/${id}`, { data: { version } });
  return singleResponseSchema.parse(data).data;
}

/** Exclusão definitiva — backend só aceita agendamento já cancelado. */
export async function deleteRecurringInvoicePermanently(id: string): Promise<void> {
  await apiClient.delete(`/api/fiscal/recurring/${id}/permanent`);
}
